/**
 * Validation error messages for Bulk Upload (Checklist & Trial Balance)
 */

export const CHECKLIST_VALIDATION_MESSAGES = {
  DESCRIPTION_REQUIRED: 'Task Name is required',
  CATEGORY_REQUIRED: 'Category is required',
  SUBCATEGORY_REQUIRED: 'Subcategory is required',
  LOCATION_REQUIRED: 'Location is required',
  PREPARER_REQUIRED: 'At least one preparer is required',
  APPROVER_REQUIRED: 'At least one approver is required',
  TAGS_REQUIRED: 'Frequency is required',
  PREPARER_APPROVER_OVERLAP: 'cannot be both preparer and approver',
  DUPLICATE_IN_ROLE: 'is listed more than once as',
  INACTIVE_PREPARER: 'Inactive preparer(s)',
  INACTIVE_APPROVER: 'Inactive approver(s)',
  INACTIVE_SIGN_OFF: 'Inactive sign-off approver(s)',
  USER_NOT_FOUND: (emails) => `User not found: ${[].concat(emails).join(', ')}`,
  INVALID_GL_CODE: 'Invalid GL Code(s)',
  GL_NOT_AVAILABLE_AT_LOCATION: (gl, locationName) =>
    `GL code ${gl} is not available for location ${locationName}`,
  INVALID_CATEGORY: 'Invalid Category. It must be from Chart of Accounts',
  INVALID_SUBCATEGORY: 'Invalid Sub Category. It must be from Chart of Accounts',
  INVALID_NUMBER: 'is not a valid number',
  INVALID_DATE: 'is not a valid date',
  INVALID_LOCATION: 'Invalid location(s)',
  DUE_DAYS_OUT_OF_RANGE: 'Due Days must be between -200 and 200'
};

export const TRIAL_BALANCE_VALIDATION_MESSAGES = {
  GL_CODE_REQUIRED: 'GL Code is required',
  GL_CODE_NOT_FOUND: 'GL Code not found in Chart of Accounts',
  DUPLICATE_ACCOUNT_EMPTY_LOCATION: 'Account No already exists with empty Location.',
  LOCATION_NOT_EXIST: (location) => `Location ${location} does not exist`,
};

export const GENERAL_ERROR_MESSAGES = {
  FAILED_LOAD_CHART_OF_ACCOUNTS: 'Failed to load Chart of Accounts for GL validation',
  FAILED_LOAD_LOCATIONS: 'Failed to load location list',
  EXTENSION_NOT_SUPPORTED: 'Only xlsx, xls, xlsm, xlsb file extensions supported',
  ERROR_READING_GLCODES: 'Error reading glcodes',
  ERROR_UPLOADING_CHECKLIST: 'Error uploading Tasklist',
  ERROR_UPLOADING_TB: 'Error uploading Trial Balance',
  UNKNOWN_ERROR: 'Unknown error!'
};

/**
 * Values that appear more than once in a list (case-insensitive).
 * Returns the first occurrence spelling of each duplicate.
 * @param {Array<string>} values
 * @returns {string[]}
 */
export function findDuplicateValues(values = []) {
  const seen = new Set();
  const duplicates = [];
  const reported = new Set();
  (Array.isArray(values) ? values : []).forEach((raw) => {
    const trimmed = String(raw || '').trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      if (!reported.has(key)) {
        duplicates.push(trimmed);
        reported.add(key);
      }
      return;
    }
    seen.add(key);
  });
  return duplicates;
}

/**
 * @param {string[]} duplicates
 * @param {string} roleLabel
 * @returns {string}
 */
export function duplicateInRoleMessage(duplicates, roleLabel) {
  return `'${duplicates.join(', ')}' ${CHECKLIST_VALIDATION_MESSAGES.DUPLICATE_IN_ROLE} ${roleLabel}`;
}

/**
 * Message when the user tries to add a person already in the same role list.
 * @param {string} value - email or display name
 * @param {string} [roleLabel]
 * @returns {string}
 */
export function duplicateAttemptMessage(value, roleLabel) {
  const label = String(value || '').trim();
  if (roleLabel && label) return duplicateInRoleMessage([label], roleLabel);
  if (label) return `${label} already exists.`;
  return 'This user is already selected.';
}

/**
 * Canonical Excel/preview labels for bulk add/edit.
 * Accepts legacy "Description" / "Task Description" and "Tags" on upload.
 * @param {*} header
 * @returns {string}
 */
export function canonicalizeBulkTaskHeader(header = '') {
  const trimmed = String(header ?? '').trim();
  const key = trimmed.toLowerCase();
  if (key === 'description' || key === 'task description' || key === 'task name') {
    return 'Task Name';
  }
  if (key === 'tags' || key === 'frequency') {
    return 'Frequency';
  }
  return trimmed;
}

export const HEADER_ERROR_MESSAGES = {
  MISSING_COLUMN: (columnName, position) => `Missing column '${columnName}' at position ${position}`,
  WRONG_COLUMN: (expected, found, position) => `Expected '${expected}' at position ${position} but found '${found}'`,
  EXTRA_COLUMN: (columnName, position) => `Unexpected extra column '${columnName}' at position ${position}`,
  HEADER_MISMATCH: 'Column headers mismatch detected:',
  EXPECTED_HEADERS: (headers) => `Expected headers: '${headers.join("', '")}'`,
  FOUND_HEADERS: (headers) => `Found headers: '${headers.join("', '")}'`,
  ENSURE_ORDER: 'Please ensure columns are in the correct order.'
};
